import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Snackbar from '@material-ui/core/Snackbar';
import GetAppIcon from '@material-ui/icons/GetApp';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import DoctorTypeBadge from '../shared/DoctorTypeBadge';
import { DOCTOR_TYPE, FEE_MODEL } from '../shared/constants';

const useStyles = makeStyles(() => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  searchInput: { minWidth: 220 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  totalRow: {
    '& td': { fontWeight: 700, fontSize: '0.85rem', borderTop: '2px solid #333' },
  },
  summaryCard: {
    padding: 20,
    borderRadius: 8,
    textAlign: 'center',
    color: '#fff',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  summaryValue: { fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.2 },
  summaryLabel: { fontSize: '0.8rem', marginTop: 4, opacity: 0.9 },
  feeModelChip: {
    fontWeight: 500,
    fontSize: '0.7rem',
    height: 22,
    borderRadius: 11,
  },
  agingCard: {
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    border: '2px solid',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  agingValue: { fontSize: '1.5rem', fontWeight: 700 },
  agingLabel: { fontSize: '0.8rem', marginTop: 4, color: '#555' },
  exportBtn: {
    marginLeft: 'auto',
  },
  statusPaid: { color: '#4CAF50', fontWeight: 600 },
  statusPending: { color: '#F44336', fontWeight: 600 },
}));

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtUGX = (amount) =>
  `UGX ${amount.toLocaleString('en-UG')}`;

const FEE_MODEL_LABELS = {
  [FEE_MODEL.PERCENTAGE_SPLIT]: 'Percentage Split',
  [FEE_MODEL.FLAT_RATE]: 'Flat Rate',
  [FEE_MODEL.DIRECT_BILLING]: 'Direct Billing',
};

const FEE_MODEL_COLORS = {
  [FEE_MODEL.PERCENTAGE_SPLIT]: '#9C27B0',
  [FEE_MODEL.FLAT_RATE]: '#2196F3',
  [FEE_MODEL.DIRECT_BILLING]: '#FF9800',
};

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const EXTERNAL_DOCTORS = [
  {
    id: 1,
    name: 'Dr. Okello Patrick',
    type: DOCTOR_TYPE.VISITING_CONSULTANT,
    specialty: 'Orthopaedics',
    feeModel: FEE_MODEL.PERCENTAGE_SPLIT,
    cases: 18,
    totalFees: 28800000,
    hospitalShare: 8640000,
    doctorShare: 20160000,
    paid: 14400000,
    pending: 5760000,
  },
  {
    id: 2,
    name: 'Dr. Byaruhanga Martin',
    type: DOCTOR_TYPE.LOCUM,
    specialty: 'ENT',
    feeModel: FEE_MODEL.FLAT_RATE,
    cases: 20,
    totalFees: 16000000,
    hospitalShare: 4800000,
    doctorShare: 11200000,
    paid: 11200000,
    pending: 0,
  },
  {
    id: 3,
    name: 'Dr. Atwine Gerald',
    type: DOCTOR_TYPE.REFERRED_SPECIALIST,
    specialty: 'Cardiothoracic',
    feeModel: FEE_MODEL.DIRECT_BILLING,
    cases: 4,
    totalFees: 18000000,
    hospitalShare: 3600000,
    doctorShare: 14400000,
    paid: 9000000,
    pending: 5400000,
  },
  {
    id: 4,
    name: 'Dr. Nankya Rehema',
    type: DOCTOR_TYPE.VISITING_CONSULTANT,
    specialty: 'Plastic Surgery',
    feeModel: FEE_MODEL.PERCENTAGE_SPLIT,
    cases: 8,
    totalFees: 12000000,
    hospitalShare: 3600000,
    doctorShare: 8400000,
    paid: 4800000,
    pending: 3600000,
  },
];

const AGING_DATA = {
  pending30: { count: 3, amount: 8760000 },
  pending60: { count: 1, amount: 5400000 },
};

// ── Component ────────────────────────────────────────────────────────────────
const ExternalDoctorFeeReport = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const filtered = doctorFilter === 'all'
    ? EXTERNAL_DOCTORS
    : EXTERNAL_DOCTORS.filter((d) => d.id === parseInt(doctorFilter, 10));

  const totalFees = filtered.reduce((s, d) => s + d.totalFees, 0);
  const totalPaid = filtered.reduce((s, d) => s + d.paid, 0);
  const totalPending = filtered.reduce((s, d) => s + d.pending, 0);
  const totalHospitalShare = filtered.reduce((s, d) => s + d.hospitalShare, 0);
  const totalDoctorShare = filtered.reduce((s, d) => s + d.doctorShare, 0);

  return (
    <div>
      <PageTitle title="External Doctor Fees" />
      <SubHeader title="Surgery" subTitle="External Doctor Fees" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Filters ───────────────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
          <Typography className={classes.filterLabel}>Doctor:</Typography>
          <Select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            <MenuItem value="all">All External Doctors</MenuItem>
            {EXTERNAL_DOCTORS.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<GetAppIcon />}
            className={classes.exportBtn}
            onClick={() => setSnackbarOpen(true)}
          >
            Export to Excel
          </Button>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────────── */}
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {[
            { label: 'Total External Fees', value: totalFees, color: '#3F51B5' },
            { label: 'Total Paid Out', value: totalPaid, color: '#4CAF50' },
            { label: 'Total Pending', value: totalPending, color: '#F44336' },
          ].map((card) => (
            <Grid item xs={12} sm={4} key={card.label}>
              <Paper className={classes.summaryCard} style={{ backgroundColor: card.color }} elevation={2}>
                <Typography className={classes.summaryValue}>{fmtUGX(card.value)}</Typography>
                <Typography className={classes.summaryLabel}>{card.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Doctor Fee Table ───────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>External Doctor Fee Ledger</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Doctor Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Fee Model</TableCell>
                <TableCell align="center">Cases</TableCell>
                <TableCell align="right">Total Fees</TableCell>
                <TableCell align="right">Hospital Share</TableCell>
                <TableCell align="right">Doctor Share</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Pending</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell className={classes.tableCell}>{doc.name}</TableCell>
                  <TableCell>
                    <DoctorTypeBadge doctorType={doc.type} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={FEE_MODEL_LABELS[doc.feeModel]}
                      size="small"
                      className={classes.feeModelChip}
                      style={{
                        backgroundColor: FEE_MODEL_COLORS[doc.feeModel],
                        color: '#fff',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>{doc.cases}</TableCell>
                  <TableCell align="right" className={classes.tableCell}>
                    {doc.totalFees.toLocaleString('en-UG')}
                  </TableCell>
                  <TableCell align="right" className={classes.tableCell}>
                    {doc.hospitalShare.toLocaleString('en-UG')}
                  </TableCell>
                  <TableCell align="right" className={classes.tableCell}>
                    {doc.doctorShare.toLocaleString('en-UG')}
                  </TableCell>
                  <TableCell align="right" className={classes.statusPaid}>
                    {doc.paid.toLocaleString('en-UG')}
                  </TableCell>
                  <TableCell align="right" className={doc.pending > 0 ? classes.statusPending : classes.statusPaid}>
                    {doc.pending.toLocaleString('en-UG')}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={classes.totalRow}>
                <TableCell>Total</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="center">{filtered.reduce((s, d) => s + d.cases, 0)}</TableCell>
                <TableCell align="right">{totalFees.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">{totalHospitalShare.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">{totalDoctorShare.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">{totalPaid.toLocaleString('en-UG')}</TableCell>
                <TableCell align="right">{totalPending.toLocaleString('en-UG')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Aging Section ─────────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Fee Aging</Typography>
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          <Grid item xs={12} sm={6}>
            <Paper className={classes.agingCard} elevation={0} style={{ borderColor: '#FF9800' }}>
              <Typography className={classes.agingValue} style={{ color: '#FF9800' }}>
                {AGING_DATA.pending30.count} doctors
              </Typography>
              <Typography className={classes.agingLabel}>
                Pending &gt; 30 days &mdash; {fmtUGX(AGING_DATA.pending30.amount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper className={classes.agingCard} elevation={0} style={{ borderColor: '#F44336' }}>
              <Typography className={classes.agingValue} style={{ color: '#F44336' }}>
                {AGING_DATA.pending60.count} doctor
              </Typography>
              <Typography className={classes.agingLabel}>
                Pending &gt; 60 days &mdash; {fmtUGX(AGING_DATA.pending60.amount)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message="Export to Excel functionality will be available once the backend is connected."
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Paper>
    </div>
  );
};

export default ExternalDoctorFeeReport;
